import type {Key, SingleSelection} from '@react-types/shared';
import type {TabListState} from '@sail/react-aria';
import {useRef} from 'react';
import type {Collection, Item} from './hooks';
import {useSingleSelectListState} from './react-aria-list';

/**
 * A replacement for
 * [useTabListState](https://react-spectrum.adobe.com/react-stately/useTabListState.html)
 * from react-stately. Pass `getItems` from `useCollection` in here to produce a
 * list of items.
 */
export function useTabListState<T>({
  getItems,
  filter,
  disabledKeys,
  selection,
}: {
  getItems: Collection<T>['getItems'];
  filter?: (items: Array<Item<T>>) => Array<Item<T>>;
  disabledKeys?: Set<Key>;
  selection: SingleSelection;
}): TabListState<T> {
  const state = useSingleSelectListState<T>({
    getItems,
    filter,
    disabledKeys,
    selection,
  });
  const {selectionManager, collection} = state;

  const collectionIsEmpty = !collection.size;

  const currentSelectedKey = state.selectedKey;

  const lastSelectedKey = useRef(currentSelectedKey);

  // The first render pass on useCollection is used to register items, so the collection is initially empty
  // Skip trying to initialize the current selected tab (and focus) for the first render pass
  if (!collectionIsEmpty) {
    // Ensure a tab is always selected (in case no selected key was specified or if selected item was deleted from collection)
    let selectedKey = currentSelectedKey;
    if (selectionManager.isEmpty || !collection.getItem(selectedKey)) {
      selectedKey = collection.getFirstKey() as Key;
      // loop over tabs until we find one that isn't disabled and select that
      while (
        state.disabledKeys.has(selectedKey) &&
        selectedKey !== collection.getLastKey()
      ) {
        selectedKey = collection.getKeyAfter(selectedKey) as Key;
      }
      // if this check is true, then every item is disabled, it makes more sense to default to the first key than the last
      if (
        state.disabledKeys.has(selectedKey) &&
        selectedKey === collection.getLastKey()
      ) {
        selectedKey = collection.getFirstKey() as Key;
      }
      // directly set selection because replace/toggle selection won't consider disabled keys
      selectionManager.setSelectedKeys([selectedKey]);
    }

    // If the tablist doesn't have focus and the selected key changes or if there isn't a focused key yet, change focused key to the selected key if it exists.
    if (
      selectionManager.focusedKey == null ||
      (!selectionManager.isFocused && selectedKey !== lastSelectedKey.current)
    ) {
      selectionManager.setFocusedKey(selectedKey);
    }
    lastSelectedKey.current = selectedKey;
  }

  return {
    ...state,
    isDisabled: false,
  };
}
